import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, apiFetch } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Wordmark } from "@/components/brand/Wordmark";

interface InboxItem {
  to: string;
  subject: string;
  kind: string;
  link: string | null;
  token: string | null;
  created_at: string;
}

const isProduction = process.env.NODE_ENV === "production"
  && process.env.REACT_APP_ENV === "production";

export default function DevInbox() {
  const [items, setItems] = useState<InboxItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<{ items: InboxItem[] }>("/_dev/inbox", {
        method: "GET",
        auth: false,
      });
      setItems(data.items);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load inbox.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (isProduction) {
    return (
      <main className="min-h-screen grid place-items-center text-foreground">
        <p className="text-sm text-muted-foreground">Not available.</p>
      </main>
    );
  }

  return (
    <main
      data-testid="dev-inbox-page"
      className="min-h-screen bg-background text-foreground"
    >
      <header className="px-6 md:px-12 py-6 flex items-center justify-between border-b border-border">
        <Link
          to="/"
          className="font-display text-xl tracking-tight"
          data-testid="dev-inbox-wordmark"
        >
          <Wordmark className="h-8" />
        </Link>
        <span className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium">
          — Dev inbox · preview only
        </span>
      </header>

      <section className="max-w-3xl mx-auto px-6 md:px-12 py-10">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-display text-3xl">Last emails</h1>
          <Button
            type="button"
            data-testid="dev-inbox-refresh"
            onClick={load}
            className="bg-foreground hover:bg-charcoal text-ivory rounded-none h-9 px-4 text-xs uppercase tracking-[0.2em]"
          >
            Refresh
          </Button>
        </div>

        {loading && (
          <p className="text-sm text-muted-foreground">Loading…</p>
        )}
        {error && (
          <p className="text-sm text-oxblood border border-oxblood/30 bg-oxblood/5 px-3 py-2">
            {error}
          </p>
        )}
        {!loading && !error && items.length === 0 && (
          <p
            data-testid="dev-inbox-empty"
            className="text-sm text-muted-foreground"
          >
            No emails yet. Trigger a signup or password reset and refresh.
          </p>
        )}

        <ul data-testid="dev-inbox-list" className="space-y-3">
          {items.map((it, i) => (
            <li
              key={`${it.to}-${i}-${it.created_at}`}
              data-testid={`dev-inbox-item-${i}`}
              className="border border-border p-5 bg-background"
            >
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    {it.kind}
                  </p>
                  <p className="font-display text-lg mt-1">{it.subject}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    To: {it.to}
                  </p>
                </div>
                <span className="text-[11px] text-muted-foreground">
                  {new Date(it.created_at).toLocaleString()}
                </span>
              </div>
              {it.link && (
                <a
                  href={it.link}
                  data-testid={`dev-inbox-link-${i}`}
                  className="mt-4 inline-block text-sm text-oxblood underline underline-offset-4 break-all"
                >
                  {it.link}
                </a>
              )}
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}
